import React, { useState } from 'react';

const PartnerApplication = () => {
  const [formData, setFormData] = useState({
    partnerName: '',
    location: '',
    businessType: '',
    email: '',
    phone: '',
    description: '',
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!formData.partnerName.trim()) {
      newErrors.partnerName = 'Business name is required';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    if (!formData.businessType) {
      newErrors.businessType = 'Please select a business type';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Business description is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Send form data to Formspree
      const response = await fetch('https://formspree.io/f/xyyrqzgl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          _subject: 'New Partner Application',
          _email: 'support@fexlogistics.com.ng',
          _replyto: 'support@fexlogistics.com.ng'
        })
      });
      
      if (response.ok) {
        console.log('Form submitted successfully');
        setSubmitSuccess(true);
        setFormData({ 
          partnerName: '', 
          location: '', 
          businessType: '',
          email: '',
          phone: '',
          description: ''
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Form submission failed:', error);
      alert('Failed to submit application. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="container mx-auto px-4 md:px-6 py-10 bg-white">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Partner Application</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Partner with us to expand your business reach. We offer technology solutions, 
            marketing support, and access to our customer base.
          </p>
        </div>
        
        {submitSuccess ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
            <div className="text-green-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h2>
            <p className="text-gray-600 mb-6">Thank you for your interest. We've received your application and will contact you soon.</p>
            <button 
              onClick={() => setSubmitSuccess(false)}
              className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors duration-300"
            >
              Submit Another Application
            </button>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 md:p-8 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700" htmlFor="partnerName">
                    Business Name
                  </label>
                  <input
                    id="partnerName"
                    name="partnerName"
                    value={formData.partnerName}
                    onChange={handleChange}
                    className={`w-full rounded-lg bg-white border ${
                      errors.partnerName ? 'border-red-500' : 'border-gray-300'
                    } px-4 py-3 text-gray-900 outline-none transition-colors focus:border-red-600`}
                    placeholder="Enter your business name"
                  />
                  {errors.partnerName && (
                    <p className="mt-1 text-sm text-red-500">{errors.partnerName}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700" htmlFor="location">
                    Location
                  </label>
                  <input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className={`w-full rounded-lg bg-white border ${
                      errors.location ? 'border-red-500' : 'border-gray-300'
                    } px-4 py-3 text-gray-900 outline-none transition-colors focus:border-red-600`}
                    placeholder="Enter your city"
                  />
                  {errors.location && (
                    <p className="mt-1 text-sm text-red-500">{errors.location}</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700" htmlFor="businessType">
                    Business Type
                  </label>
                  <select
                    id="businessType"
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleChange}
                    className={`w-full rounded-lg bg-white border ${
                      errors.businessType ? 'border-red-500' : 'border-gray-300'
                    } px-4 py-3 text-gray-900 outline-none transition-colors focus:border-red-600`}
                  >
                    <option value="">Select business type</option>
                    <option value="restaurant">Restaurant</option>
                    <option value="retail">Retail Store</option>
                    <option value="service">Service Provider</option>
                    <option value="manufacturer">Manufacturer</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.businessType && (
                    <p className="mt-1 text-sm text-red-500">{errors.businessType}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700" htmlFor="email">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full rounded-lg bg-white border ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    } px-4 py-3 text-gray-900 outline-none transition-colors focus:border-red-600`}
                    placeholder="Enter your email"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700" htmlFor="phone">
                  Phone Number
                </label>
                <input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full rounded-lg bg-white border ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  } px-4 py-3 text-gray-900 outline-none transition-colors focus:border-red-600`}
                  placeholder="Enter your phone number"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700" htmlFor="description">
                  Business Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="4"
                  value={formData.description}
                  onChange={handleChange}
                  className={`w-full rounded-lg bg-white border ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  } px-4 py-3 text-gray-900 outline-none transition-colors focus:border-red-600`}
                  placeholder="Tell us about your business..."
                ></textarea>
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">{errors.description}</p>
                )}
              </div>
              
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full md:w-auto px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </>
                  ) : 'Apply Now'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </section>
  );
};

export default PartnerApplication;